import React, { ReactChild, useEffect, useMemo, useState } from 'react'
import { useSetAtom } from 'jotai'
import { useRouter } from 'next/router'
import NextLink from 'next/link'
import Cookies from 'universal-cookie'
import { Formik, FormikConfig } from 'formik'
import { useMutationSignIn } from '@mindfulchefuk/services/signIn'
import { verifyOtp } from '@mindfulchefuk/services/verifyOtp'

import {
  TertiaryButton,
  Box,
  Text,
  Link,
  IllustrationContainer,
} from '@mindfulchefuk/design-system'
import routesConfig from '@mindfulchefuk/config/routesConfig'
import {
  ANALYTICS_CATEGORIES,
  CUSTOMER_ID_COOKIE_KEY,
} from '@mindfulchefuk/constants'

import MainLayout from '@mindfulchefuk/layouts/MainLayout'

import { logEvent } from '@mindfulchefuk/utils/analytics'
import { NextPageWithLayout } from '@mindfulchefuk/types/NextJS'
import { SignInForm } from '@mindfulchefuk/features/UsersForms/SignInForm'
import { AuthenicateCode } from '@mindfulchefuk/features/UsersForms/AuthenicateCode'
import { CookieBanner } from '@mindfulchefuk/features/Cookies/components'
import { useDispatch } from 'react-redux'
import { addNotification } from '@mindfulchefuk/features/Notifications/notificationsSlice'
import { useSignInRedirect } from '@mindfulchefuk/features/Customer/hooks/useSignInRedirect'
import {
  setUserId,
  trackAnalyticsEvent,
} from '@mindfulchefuk/utils/analytics/amplitude/amplitudeAdapter'
import { customerIdAtom } from '@mindfulchefuk/store/customer'
import { identifyCustomerForInAppMessaging } from '@mindfulchefuk/utils/identifyCustomerForInAppMessaging'
import { REFERRER_PATH_KEY } from '@mindfulchefuk/hooks/useReferrer'

type LoginFormData = {
  email: string
  password: string
}

type VerifyFormData = {
  code: string
}

type SignInState =
  | 'signing-in'
  | 'signed-out'
  | 'signed-in'
  | 'verifying-2fa'
  | 'verify-2fa'

/*
 * We need to make sure that the user is who they say they are.
 * We do this by sending them an OTP code to their email address or phone.
 *
 * So, we need to make a request to the API to send an OTP code and present
 * the user with a form to enter the code.
 *
 * Once the user has entered the code, we need to send it to the API to
 * verify that it is correct. If it is, we can then log the user in.
 */
const Login: NextPageWithLayout = () => {
  const router = useRouter()
  const cookies = useMemo(() => new Cookies(), [])
  const isPasswordChanged = router.query.passwordChanged
  const dispatch = useDispatch()
  const setCustomerId = useSetAtom(customerIdAtom)

  // This state has to exist because reading from cookies doesn't re-rerender the component properly
  const [signInState, setSignInState] = useState<SignInState>(
    cookies.get(CUSTOMER_ID_COOKIE_KEY) ? 'signed-in' : 'signed-out'
  )
  const [authenticationState, setAuthenticationState] =
    useState<LoginFormData | null>(null)

  const [isOtaAuth, setIsOtaAuth] = useState<boolean>(false)

  const { mutate: signIn } = useMutationSignIn({
    onSuccess: (data) => {
      if (data.accessToken) {
        setCustomerId(data.customerId)
        setUserId(data.customerId)
        logEvent({
          category: ANALYTICS_CATEGORIES.signIn,
          action: 'Successful Sign In',
          label: 'Login Successful',
        })

        trackAnalyticsEvent({
          name: 'user logged in',
        })

        setIsOtaAuth(false)
        setSignInState('signed-in')

        identifyCustomerForInAppMessaging()
      } else {
        setIsOtaAuth(true)
        setAuthenticationState(null)
        setSignInState('verify-2fa')
      }
    },
  })

  const handleGetStartedClick = () => {
    logEvent({
      category: ANALYTICS_CATEGORIES.signIn,
      action: 'Get Started Button',
      label: 'Click',
    })
  }

  const handleForgotOnClick = () => {
    logEvent({
      category: ANALYTICS_CATEGORIES.signIn,
      action: 'Forgot your Password Button',
      label: 'Click',
    })
  }

  const handleVerifySubmit: FormikConfig<VerifyFormData>['onSubmit'] = async (
    { code },
    { setErrors }
  ) => {
    try {
      setSignInState('verifying-2fa')
      const customerId = await verifyOtp(
        authenticationState.email,
        authenticationState.password,
        code
      )

      if (customerId !== null) {
        setSignInState('signed-in')

        logEvent({
          category: ANALYTICS_CATEGORIES.signIn,
          action: 'Successful Sign In',
          label: 'Login Successful',
        })

        trackAnalyticsEvent({
          name: 'user logged in',
        })

        setUserId(customerId)

        setSignInState('signed-in')
      }
    } catch {
      setErrors({ code: 'login failed' })
      setSignInState('verify-2fa')
    }
  }

  const handleSubmit: FormikConfig<LoginFormData>['onSubmit'] = async (
    { email, password },
    { setErrors }
  ) => {
    setSignInState('signing-in')

    await signIn(
      { email, password },
      {
        onError: () => {
          logEvent({
            category: ANALYTICS_CATEGORIES.signIn,
            action: 'Unsuccessful Sign In',
            label: 'Login Failed',
          })

          setErrors({ password: 'login failed' })
          setSignInState('signed-out')
        },
      }
    )
  }

  const displaySignIn =
    signInState === 'signing-in' ||
    signInState === 'signed-out' ||
    signInState === 'verifying-2fa' ||
    signInState === 'verify-2fa' ||
    signInState === 'signed-in'

  const displaySignInForm =
    (signInState === 'signed-in' ||
      signInState === 'signed-out' ||
      signInState === 'signing-in') &&
    isOtaAuth === false

  const display2faOrSignedIn =
    (signInState === 'verifying-2fa' ||
      signInState === 'verify-2fa' ||
      signInState === 'signed-in') &&
    isOtaAuth === true

  const display2faForm =
    (signInState === 'verifying-2fa' ||
      signInState === 'verify-2fa' ||
      signInState === 'signed-in') &&
    isOtaAuth === true

  const displaySignedInOrOut =
    signInState === 'signed-in' || signInState === 'signed-out'

  const signedInProcess = signInState === 'signed-in'

  useSignInRedirect(signInState === 'signed-in')

  useEffect(() => {
    let sanitisedPageSource = 'other'
    const pageSource = window.sessionStorage.getItem(REFERRER_PATH_KEY) || ''

    // NOTE: Next.js reverts to home page for paths it cant match and there is no additional directory lookup
    const isHomepageSource =
      /^\/$/i.test(pageSource) || !pageSource.substring(1).includes('/')

    if (isHomepageSource) {
      sanitisedPageSource = 'homepage navigation'
    } else if (pageSource.includes('/checkout/about-you')) {
      sanitisedPageSource = 'recipe onboarding - about you'
    }

    trackAnalyticsEvent({
      name: 'account log in page viewed',
      data: {
        'page source': sanitisedPageSource,
      },
    })
  }, [])

  useEffect(() => {
    if (isPasswordChanged) {
      dispatch(addNotification({ id: 'success-password-changed' }))
    }
  }, [dispatch, isPasswordChanged])

  // Break up components into legacy and 2FA https://mindfulchef.atlassian.net/browse/MIN-11634
  return (
    <Box overflow="hidden">
      <IllustrationContainer
        color="aubergine"
        pt={{ base: 24, sm: 36 }}
        pb={32}
        illustrations="10"
      >
        {displaySignIn && (
          <Box mx="auto" maxWidth="440px">
            {displaySignedInOrOut && (
              <>
                <Text as="h1" variant="heading-lg" textAlign="center">
                  Sign in
                </Text>
                <Text
                  as="p"
                  variant="body-lg"
                  mt={{ base: 12, sm: 16 }}
                  textAlign="center"
                >
                  Welcome back!
                </Text>
              </>
            )}

            {displaySignInForm && (
              <Formik
                initialValues={
                  {
                    email: '',
                    password: '',
                  } as LoginFormData
                }
                onSubmit={handleSubmit}
              >
                <SignInForm isDisabled={signInState !== 'signed-out'} />
              </Formik>
            )}

            {display2faForm && (
              <Text as="h1" variant="heading-lg" textAlign="center">
                Verify your account
              </Text>
            )}

            {display2faOrSignedIn && (
              <Text
                as="p"
                variant="body-lg"
                mt={{ base: 12, sm: 16 }}
                textAlign="center"
              >
                We&apos;ve sent a verification code to you at{' '}
                {authenticationState?.email}, simply check your email and paste
                your code here to continue.
              </Text>
            )}

            {display2faForm && (
              <Formik
                initialValues={
                  {
                    code: '',
                  } as VerifyFormData
                }
                onSubmit={handleVerifySubmit}
              >
                <AuthenicateCode isDisabled={signInState !== 'verify-2fa'} />
              </Formik>
            )}

            {displaySignInForm && (
              <Box textAlign="center" mt={24}>
                <NextLink href={routesConfig.forgotPassword.pathname} passHref>
                  <TertiaryButton
                    onClick={handleForgotOnClick}
                    variant="primary"
                    size="medium"
                    as="a"
                  >
                    Forgot your password?
                  </TertiaryButton>
                </NextLink>
              </Box>
            )}

            {signedInProcess && (
              <Text variant="body-xs" color="broccoli" mt={4} textAlign="left">
                Signed in successfully, redirecting...
              </Text>
            )}
          </Box>
        )}
      </IllustrationContainer>

      <Box backgroundColor="salt" color="aubergine" textAlign="center">
        <Text variant="body-xl" py={{ base: 12, sm: 24 }} px={24}>
          Don’t have an account yet?{' '}
          <NextLink href={routesConfig.chooseRecipes.pathname} passHref>
            <Link
              variant="body-xl"
              onClick={handleGetStartedClick}
              href={routesConfig.chooseRecipes.pathname}
            >
              Get started
            </Link>
          </NextLink>
        </Text>
      </Box>
    </Box>
  )
}

Login.getLayout = (page: ReactChild) => (
  <MainLayout
    title={routesConfig.signIn.label}
    description="Log in to My Account to get more from Mindful Chef."
  >
    {page}
    <CookieBanner />
  </MainLayout>
)

export default Login

export { getStaticGlobalContent as getStaticProps } from '@mindfulchefuk/features/CMS/services/getStaticData'
